import { useState, useEffect } from "react";
import { 
  X, 
  Search, 
  BookOpen, 
  User, 
  Barcode, 
  Calendar, 
  ChevronLeft, 
  ChevronRight, 
  Inbox, 
  RefreshCw 
} from "lucide-react";
import toast from "react-hot-toast";
import api from "../../connector/api";

export default function ReturnedBooksModal({ onClose }) {
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const PAGE_SIZE = 15; // Kept static to protect mathematical pagination logic
  const [search, setSearch] = useState("");

  const fetchReturnedLoans = async (p = 1, q = "") => {
    try {
      setLoading(true);
      const res = await api.get("/loans/returned/", {
        params: { page: p, search: q },
      });
      setLoans(res.data.results || []);
      setCount(res.data.count || 0);
      setPage(p);
    } catch (err) {
      console.error(err);
      toast.error("Failed to sync system logs.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReturnedLoans();
  }, []);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    fetchReturnedLoans(1, search);
  };

  const handleClearSearch = () => {
    setSearch("");
    fetchReturnedLoans(1, "");
  };

  const totalPages = Math.ceil(count / PAGE_SIZE) || 1;

  return (
    <div className="fixed inset-0 bg-stone-950/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
      <div className="bg-[#1a1613] w-full max-w-6xl rounded-2xl shadow-2xl border border-[#2e2720] flex flex-col max-h-[90vh] overflow-hidden transform transition-all font-dynamic-title">
        
        {/* Terminal Header */}
        <div className="px-6 py-4 border-b border-[#2e2720] flex items-center justify-between bg-[#131110] shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-amber-500/10 border border-amber-500/20 rounded-xl text-amber-400">
              <BookOpen className="h-5 w-5" />
            </div>
            <div>
              <h3 className="text-base font-bold text-stone-100">Returned Asset Ledger</h3>
              <p className="text-xs text-stone-400 mt-0.5">Historical verification record logs and circulation archive</p>
            </div>
          </div>
          <button 
            onClick={onClose} 
            className="text-stone-400 hover:text-stone-200 hover:bg-[#201c17] p-1.5 rounded-xl transition"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Filter & Action Controls Panel */}
        <div className="p-4 bg-[#131110] border-b border-[#2e2720] flex flex-col sm:flex-row items-center gap-3 shrink-0">
          <form onSubmit={handleSearchSubmit} className="relative w-full sm:flex-1">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-500" />
            <input
              type="text"
              placeholder="Filter by patron identifier or book metrics..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full bg-[#1a1613] border border-[#2e2720] text-stone-200 pl-10 pr-16 py-2 rounded-xl text-xs placeholder-stone-500 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500/50 transition font-mono"
            />
            {search && (
              <button
                type="button"
                onClick={handleClearSearch}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] font-semibold text-stone-400 hover:text-stone-200 px-1.5 py-0.5 rounded hover:bg-[#201c17] transition"
              >
                Clear
              </button>
            )}
          </form>
          
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <button
              type="button"
              onClick={() => fetchReturnedLoans(1, search)}
              className="flex-1 sm:flex-initial justify-center px-4 py-2 bg-amber-500 hover:bg-amber-400 text-stone-950 text-xs font-bold rounded-xl shadow transition flex items-center gap-1.5"
            >
              <span>Apply Filter</span>
            </button>
            <button
              type="button"
              onClick={() => fetchReturnedLoans(page, search)}
              className="p-2 bg-[#1a1613] border border-[#2e2720] text-stone-400 hover:text-stone-200 rounded-xl hover:bg-[#201c17] transition shadow-sm"
              title="Refresh ledger state"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin text-amber-400" : ""}`} />
            </button>
          </div>
        </div>

        {/* Core Ledger Data Table View */}
        <div className="flex-1 overflow-auto bg-[#1a1613] min-h-[300px]">
          <table className="w-full text-xs text-left border-separate border-spacing-0">
            <thead className="bg-[#131110] text-stone-400 text-[10px] font-bold uppercase tracking-wider sticky top-0 z-10 border-b border-[#2e2720]">
              <tr>
                <th className="px-6 py-3 border-b border-[#2e2720] font-bold">
                  <span className="flex items-center gap-1.5"><User className="h-3.5 w-3.5 text-stone-500" /> Patron Information</span>
                </th>
                <th className="px-6 py-3 border-b border-[#2e2720] font-bold">Card Number</th>
                <th className="px-6 py-3 border-b border-[#2e2720] font-bold">
                  <span className="flex items-center gap-1.5"><BookOpen className="h-3.5 w-3.5 text-stone-500" /> Checked Out Title</span>
                </th>
                <th className="px-6 py-3 border-b border-[#2e2720] font-bold">
                  <span className="flex items-center gap-1.5"><Barcode className="h-3.5 w-3.5 text-stone-500" /> Item Barcode</span>
                </th>
                <th className="px-6 py-3 border-b border-[#2e2720] font-bold">
                  <span className="flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5 text-stone-500" /> Processed On</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#2e2720] text-stone-300">
              {loading ? (
                // Dark Theme Skeleton Loading States
                Array.from({ length: 5 }).map((_, idx) => (
                  <tr key={idx} className="animate-pulse">
                    <td className="px-6 py-4"><div className="h-4 bg-[#28221b] rounded-md w-32" /></td>
                    <td className="px-6 py-4"><div className="h-4 bg-[#28221b] rounded-md w-20" /></td>
                    <td className="px-6 py-4"><div className="h-4 bg-[#28221b] rounded-md w-48" /></td>
                    <td className="px-6 py-4"><div className="h-4 bg-[#28221b] rounded-md w-24" /></td>
                    <td className="px-6 py-4"><div className="h-4 bg-[#28221b] rounded-md w-28" /></td>
                  </tr>
                ))
              ) : loans.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-16 text-center">
                    <div className="flex flex-col items-center justify-center text-stone-500 gap-2">
                      <Inbox className="h-10 w-10 stroke-[1.2] text-stone-600" />
                      <p className="text-xs font-bold text-stone-300">No system return history found.</p>
                      <p className="text-[11px] text-stone-500 max-w-xs leading-relaxed">
                        Adjust your parameters or execute a system sync to confirm database updates.
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                loans.map((l) => (
                  <tr key={l.id} className="hover:bg-[#131110]/50 transition-colors">
                    <td className="px-6 py-3.5 font-sans font-semibold text-stone-100 max-w-[200px] truncate">
                      {l.patron_name}
                    </td>
                    <td className="px-6 py-3.5 font-mono text-xs text-stone-400">
                      {l.patron?.cardnumber || "—"}
                    </td>
                    <td className="px-6 py-3.5 font-serif tracking-wider font-medium text-stone-200 max-w-xs truncate" title={l.book.title}>
                      {l.book.title}
                    </td>
                    <td className="px-6 py-3.5 font-mono text-xs text-stone-400">
                      <span className="bg-[#131110] text-stone-300 px-2 py-0.5 rounded-md border border-[#2e2720]">
                        {l.book.barcode}
                      </span>
                    </td>
                    <td className="px-6 py-3.5 text-xs text-stone-400 font-mono">
                      {l.returned_date}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Modal Pagination Footer */}
        <div className="px-6 py-4 border-t border-[#2e2720] bg-[#131110] flex items-center justify-between shrink-0">
          <p className="text-xs font-semibold text-stone-400 uppercase tracking-wider">
            Total Logs: <span className="text-stone-100 font-bold font-mono">{count}</span>
          </p>

          <div className="flex items-center gap-4">
            <span className="text-xs text-stone-400">
              Page <strong className="text-stone-200 font-mono">{page}</strong> of <strong className="text-stone-200 font-mono">{totalPages}</strong>
            </span>

            <div className="flex items-center gap-1.5">
              <button
                type="button"
                disabled={page === 1 || loading}
                onClick={() => fetchReturnedLoans(page - 1, search)}
                className="p-1.5 bg-[#1a1613] border border-[#2e2720] text-stone-300 hover:text-stone-100 rounded-lg shadow-sm hover:bg-[#201c17] disabled:opacity-30 disabled:cursor-not-allowed transition"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                disabled={page >= totalPages || loading}
                onClick={() => fetchReturnedLoans(page + 1, search)}
                className="p-1.5 bg-[#1a1613] border border-[#2e2720] text-stone-300 hover:text-stone-100 rounded-lg shadow-sm hover:bg-[#201c17] disabled:opacity-30 disabled:cursor-not-allowed transition"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}